import React, { useState, useEffect } from 'react';
import { FileText, Search, Loader2, Download, Eye, Calendar, DollarSign, Filter } from 'lucide-react';
import { getPayments } from '../../api/wholesale/payments';
import { getOrders } from '../../api/wholesale/orders';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import ReceiptPreviewModal from '../../components/wholesale/ReceiptPreviewModal';
import ProformaInvoicePreviewModal from '../../components/wholesale/ProformaInvoicePreviewModal';

const DocumentsPage = () => {
  const { getDateRangeParams } = useFiscalPeriod();
  const [activeTab, setActiveTab] = useState('receipts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showProformaModal, setShowProformaModal] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, [activeTab, searchQuery]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const dateParams = getDateRangeParams();
      const params = { ...dateParams };
      
      if (searchQuery) {
        params.search = searchQuery;
      }

      let response;
      if (activeTab === 'receipts') {
        // Fetch payments
        response = await getPayments(params);
        setData(response.data || []);
      } else {
        // Fetch orders with proforma invoice
        // We filter for orders that have proforma_invoice_number
        // Ideally the backend API should support a filter for this, 
        // but for now we'll fetch orders and filter client-side if needed
        // or rely on a specific status if available.
        // Assuming getOrders supports rudimentary filtering or we filter the result.
        response = await getOrders({ ...params, limit: 100 }); 
        const orders = response.data || [];
        setData(orders.filter(o => o.proforma_invoice_number));
      }
    } catch (err) {
      console.error(`Error fetching ${activeTab}:`, err);
      setError(`Failed to load ${activeTab}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (item) => {
    setSelectedDocument(item);
    if (activeTab === 'receipts') {
      setShowReceiptModal(true);
    } else {
      setShowProformaModal(true);
    }
  };

  const renderReceiptsTable = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distributor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order No</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">No receipts found</td>
              </tr>
            ) : (
              data.map((receipt) => (
                <tr 
                  key={receipt.id} 
                  onClick={() => handleRowClick(receipt)}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-emerald-600">
                    {receipt.payment_number || `REC-${receipt.id}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(receipt.payment_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {receipt.distributor_business_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ₦{parseFloat(receipt.payment_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                    {receipt.payment_method}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {receipt.order_number}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderProformaTable = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proforma No</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distributor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-gray-500">No proforma invoices found</td>
              </tr>
            ) : (
              data.map((order) => (
                <tr 
                  key={order.id} 
                  onClick={() => handleRowClick(order)}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                    {order.proforma_invoice_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(order.proforma_generated_at || order.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.distributor_business_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ₦{parseFloat(order.total_amount).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium 
                      ${order.order_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                        order.order_status === 'confirmed' ? 'bg-blue-100 text-blue-800' : 
                        'bg-gray-100 text-gray-800'}`}>
                      {order.order_status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
          <p className="text-gray-500 mt-1">Track receipts and proforma invoices</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('receipts')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2
              ${activeTab === 'receipts'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            <DollarSign className="w-4 h-4" />
            Receipts
          </button>
          <button
            onClick={() => setActiveTab('proforma')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2
              ${activeTab === 'proforma'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            <FileText className="w-4 h-4" />
            Proforma Invoices
          </button>
        </nav>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${activeTab === 'receipts' ? 'receipts' : 'proformas'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      ) : (
        activeTab === 'receipts' ? renderReceiptsTable() : renderProformaTable()
      )}

      {/* Receipt Modal */}
      {showReceiptModal && selectedDocument && (
        <ReceiptPreviewModal
          receipt={{
            ...selectedDocument,
            receipt_number: selectedDocument.payment_number // Map payment_number to receipt_number for modal compatibility
          }}
          order={{
            id: selectedDocument.order_id,
            order_number: selectedDocument.order_number,
            distributor_business_name: selectedDocument.distributor_business_name,
            distributor_phone: selectedDocument.distributor_phone
          }}
          onClose={() => {
            setShowReceiptModal(false);
            setSelectedDocument(null);
          }}
        />
      )}

      {/* Proforma Modal */}
      {showProformaModal && selectedDocument && (
        <ProformaInvoicePreviewModal
          order={selectedDocument}
          onClose={() => {
            setShowProformaModal(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
};

export default DocumentsPage;
